var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SelectableIterator_1;
import React, { Component } from 'react';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { EditorContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, expression, object, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { SelectEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, isFunction, isMobile, propagateData } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as SlabPosition } from '../../../resources/assets/components/DLS_SlabPosition.svg';
import { Checkbox } from '../checkbox/checkbox.js';
import { DraggableList } from '../draggable-list/draggable-list.js';
import { Loader } from '../loader/loader.js';
import { NotFound } from '../not-found/not-found.js';
import { SelectableIteratorAPI } from './selectable-iterator-api.js';
import './selectable-iterator.css';
const SelectableIteratorContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.DATA_DISPLAY,
    ],
    name: '',
};
let SelectableIterator = SelectableIterator_1 = class SelectableIterator extends Component {
    constructor() {
        super(...arguments);
        this.onDragEnd = (result) => {
            const { onSort } = this.props;
            if (isFunction(onSort)) {
                onSort(result);
            }
        };
        this.getClassName = () => {
            const { separator } = this.props;
            return classNames({
                'c-selectable-iterator': true,
                'c-selectable-iterator--with-separator': separator,
            });
        };
    }
    onSelectItem(item) {
        const { multiselect, onSelect, selected, primaryKey } = this.props;
        if (!isFunction(onSelect)) {
            return;
        }
        const isSelected = this.isSelected(item);
        let value = (isSelected && !multiselect) || !isSelected ? [item] : [];
        if (multiselect) {
            const getPK = (item) => String(item[primaryKey]);
            const itemPK = getPK(item);
            value = !isSelected
                ? [...selected, item]
                : selected.filter((item) => itemPK !== getPK(item));
        }
        onSelect(new SelectEvent({
            selected: !isSelected,
            value,
        }));
    }
    isSelected(item) {
        const { name, primaryKey, selected = [] } = this.props;
        let selectedItems = selected;
        if (name) {
            selectedItems = selected.filter((item) => item && item.__name === name);
        }
        return selectedItems
            .map((selectedItem) => selectedItem && selectedItem[primaryKey])
            .includes(item[primaryKey]);
    }
    isSortable() {
        const { sortable = SelectableIterator_1.defaultProps.sortable, } = this.props;
        return this.context?.isDesigning() ? false : sortable;
    }
    renderCheckbox(key, selected) {
        const { multiselect, uuid } = this.props;
        if (!multiselect) {
            return null;
        }
        return (React.createElement(Checkbox, { className: "c-selectable-iterator__item-checkbox", name: `${uuid}-${key}`, uuid: "selectable-iterator-uuid", value: selected }));
    }
    renderItems() {
        const { children, data = [], primaryKey } = this.props;
        if (this.context?.isDesigning()) {
            return children;
        }
        const inheritedProps = {
            className: 'c-selectable-iterator__component',
        };
        const sortable = this.isSortable();
        const renderItem = (item) => {
            const key = String(item[primaryKey]);
            const selected = this.isSelected(item);
            const className = classNames({
                'c-selectable-iterator__item': true,
                'is-selected': selected,
            });
            return (React.createElement("div", { className: className, key: key, onClick: () => this.onSelectItem(item), role: "button", tabIndex: 0 },
                this.renderCheckbox(key, selected),
                propagateData(children, item, inheritedProps, true),
                sortable ? (React.createElement(SlabPosition, { className: "c-selectable-iterator__item-icon" })) : null));
        };
        return (React.createElement(DraggableList, { canDrag: sortable, data: data, onDragEnd: this.onDragEnd, renderItem: renderItem }));
    }
    renderLoading() {
        const { loading } = this.props;
        if (!loading) {
            return null;
        }
        const size = isMobile() ? 32 : 64;
        return (React.createElement(Loader, { centered: true, className: "c-selectable-iterator__loader u-hide@empty", size: size }));
    }
    renderNotFound() {
        const { data, done, emptyResultExpression, showNotFound } = this.props;
        if (!data || !Array.isArray(data)) {
            return null;
        }
        if (showNotFound && done && data.length === 0) {
            return React.createElement(NotFound, { className: "c-selectable-iterator__not-found", showImage: false, titleExpression: emptyResultExpression });
        }
        return null;
    }
    render() {
        return (React.createElement(Container, { acceptedTypes: SelectableIteratorContainer.acceptedTypes, className: this.getClassName(), repeated: true, uuid: this.props.uuid },
            this.renderItems(),
            this.renderNotFound(),
            this.renderLoading()));
    }
};
SelectableIterator.contextType = EditorContext;
SelectableIterator.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: arrayOf(object).notEnumerable(),
    done: bool.notEnumerable(),
    emptyResultExpression: expression,
    loading: bool,
    multiselect: bool,
    name: string,
    onSelect: eventListener,
    primaryKey: string,
    selected: arrayOf(object),
    separator: bool,
    showNotFound: bool.notEnumerable(),
    sortable: bool.notEnumerable(),
    uuid: string,
};
SelectableIterator.defaultProps = {
    multiselect: false,
    primaryKey: 'id',
    selected: [],
    separator: true,
    showNotFound: true,
    sortable: false,
};
SelectableIterator = SelectableIterator_1 = __decorate([
    AwesomeComponent({ api: SelectableIteratorAPI, containers: [SelectableIteratorContainer], type: ComponentType.LIST })
], SelectableIterator);
export { SelectableIterator, };
//# sourceMappingURL=selectable-iterator.js.map